/**
 * Sales Deal module types
 *
 * API-facing types for the Sales module's deal management surface.
 * These types describe the JSON wire format exchanged between the
 * API and the frontend - dates are ISO strings as serialised by JSON.
 */
// ── Deal category ─────────────────────────────────────────────────────────────
/**
 * Default one-line description per built-in deal category, keyed by `valueKey`. Shown under the
 * category chips at deal creation. It lives in code (not the seed) because `seedDealAttributes`
 * is provision-once and never touches a workspace that already has category rows. A workspace's
 * own `metadata.description` (DealCategoryMetadata) wins over these.
 * Copy: at most 12 words each.
 */
export const DEAL_CATEGORY_DEFAULT_DESCRIPTIONS = {
    greenfield: 'New implementation. The client is not on the system yet.',
    continuous_improvement: 'Client is already live. Add features or fix what exists.',
    bpr: 'Review how the client works today and recommend what to change.',
    dev_stories: 'Custom builds priced story by story: scripts, integrations, extensions.',
    managed_services: 'Ongoing monthly support for a live system.',
    data_platform_build: 'Build a new data platform or warehouse.',
    data_migration: 'Move data or legacy systems onto a modern platform.',
    data_managed_service: 'Ongoing support for a live data platform.',
};
// ── Ad-hoc questions ────────────────────────────────────────────────────────────
/**
 * The deal-scoped "Additional Information" group: always shown in the Company section's left nav
 * (below Primary Contact) even when empty, and the target for the in-deal "Add question" button.
 * The frontend injects a synthetic empty instance until the first field materializes the real
 * group via ensureGroup on the backend. Deliberately NOT a mandatory group (it must not be
 * server-side created, which would leak into SOW/AI flows).
 */
export const ADDITIONAL_INFO_GROUP = {
    key: 'additional_information',
    label: 'Additional Information',
};
/**
 * The focused set of system controls a one-time (ad-hoc) question can use - both the
 * controls the AI discovery pass may propose and the controls the user may pick when
 * adding an ad-hoc question by hand. A deliberately smaller set than the full template
 * authoring catalog: these are quick one-off scope questions, not reusable fields.
 * Shared so the picker, the API validator, and the AI runtime stay in sync.
 */
export const ADHOC_QUESTION_CONTROLS = [
    'text',
    'textarea',
    'number',
    'currency',
    'date',
    'boolean',
    'select',
    'multi_select',
];
/** Picker options (value + human label) for the ad-hoc add-question control selector. */
export const ADHOC_QUESTION_CONTROL_OPTIONS = [
    { value: 'text', label: 'Short text' },
    { value: 'textarea', label: 'Long text' },
    { value: 'number', label: 'Number' },
    { value: 'currency', label: 'Currency' },
    { value: 'date', label: 'Date' },
    { value: 'boolean', label: 'Yes / No' },
    { value: 'select', label: 'Single choice' },
    { value: 'multi_select', label: 'Multiple choice' },
];
//# sourceMappingURL=sales-deal.js.map